import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  setDoc,
  serverTimestamp,
  QueryConstraint,
  onSnapshot,
} from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { db, storage } from './firebase'

export async function getDocuments<T>(collectionPath: string, constraints: QueryConstraint[] = []) {
  const colRef = collection(db, collectionPath)
  const q = query(colRef, ...constraints)
  const snapshot = await getDocs(q)
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as T))
}

export async function getDocument<T>(collectionPath: string, id: string) {
  const docRef = doc(db, collectionPath, id)
  const snapshot = await getDoc(docRef)
  if (snapshot.exists()) {
    return { id: snapshot.id, ...snapshot.data() } as T
  }
  return null
}

export async function createDocument(collectionPath: string, data: any) {
  const colRef = collection(db, collectionPath)
  const docRef = await addDoc(colRef, {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
  return docRef.id
}

export async function upsertDocument(collectionPath: string, id: string, data: any) {
  const docRef = doc(db, collectionPath, id)
  await setDoc(docRef, {
    ...data,
    updatedAt: serverTimestamp(),
    createdAt: data.createdAt || serverTimestamp(),
  }, { merge: true })
}

export async function updateDocument(collectionPath: string, id: string, data: any) {
  const docRef = doc(db, collectionPath, id)
  await updateDoc(docRef, {
    ...data,
    updatedAt: serverTimestamp(),
  })
}

export async function deleteDocument(collectionPath: string, id: string) {
  const docRef = doc(db, collectionPath, id)
  await deleteDoc(docRef)
}

export async function uploadFile(path: string, file: File) {
  const storageRef = ref(storage, path)
  const snapshot = await uploadBytes(storageRef, file)
  return getDownloadURL(snapshot.ref)
}

export function subscribeToCollection<T>(collectionPath: string, callback: (items: T[]) => void) {
  const colRef = collection(db, collectionPath)
  return onSnapshot(colRef, 
    (snapshot) => {
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as T))
      callback(items)
    },
    (error) => {
      console.error(`[Firestore CRUD] Error in subscribeToCollection for ${collectionPath}:`, error)
      callback([]) // Fallback to empty items on error to keep UI stable
    }
  )
}
